import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { useTrackImpressionEventByIntersection } from "shared/hooks";

import { PlusPaywallSuspense } from "scmp-app/components/plus/plus-paywall-suspense";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { highlightBlockQuery$key } from "scmp-app/queries/__generated__/highlightBlockQuery.graphql";

import { HighlightBlockItem } from "./highlight-block-item";
import {
  Container,
  ContentContainer,
  Heading,
  PlaceHolderContainer,
  StyledImagePlaceholder,
} from "./style";

export type Props = {
  className?: string;
  reference: highlightBlockQuery$key;
};

export const HighlightBlock: FunctionComponent<Props> = ({ className, reference }) => {
  const content = useFragment(
    graphql`
      fragment highlightBlockQuery on Query @argumentDefinitions(queueName: { type: "String!" }) {
        highlightQueue: queue(filter: { name: $queueName }) {
          items(first: 6) {
            edges {
              node {
                ... on Content {
                  entityId
                  ...highlightBlockItemContent
                }
              }
            }
          }
        }
      }
    `,
    reference,
  );
  const listOfArticles = content.highlightQueue?.items?.edges
    .map?.(item => item.node.entityId)
    .join(",");
  const ga4ImpressionEvent = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles ?? "",
        page_type: "homepage",
        widget_name: "highlight_from_plus",
      },
      subcategory: "widget",
    }),
    [listOfArticles],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: ga4ImpressionEvent,
      options: {
        isSendGA4Tracking: true,
        shouldSendOnce: true,
      },
    });

  return (
    <PlusPaywallSuspense
      fallback={
        <PlaceHolderContainer>
          <StyledImagePlaceholder />
        </PlaceHolderContainer>
      }
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <Heading>HIGHLIGHTS FROM SCMP PLUS</Heading>
        <ContentContainer>
          {content.highlightQueue?.items?.edges.map(item => (
            <HighlightBlockItem key={item.node.entityId} reference={item.node} />
          ))}
        </ContentContainer>
      </Container>
    </PlusPaywallSuspense>
  );
};

HighlightBlock.displayName = "HighlightBlock";
